import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import { MetricColumn } from '@components/Common/MetricTable';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { Alert, Box, Chip } from '@mui/material';
import AiUsageSection from './AiUsageSection';
import AllCostsPanel from './AllCostsPanel';
import GooglePanel from './GooglePanel';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  breakdownRows,
  budgetTone,
  isProblem,
  categoryLabel,
  LineItem,
  lineItemLabel,
  monthLabel,
  monthRange,
  openAiDailyRows,
  OpenAIUsage,
  providerDailyRows,
  ProviderUsage,
  usd,
  usdWhole,
} from './costData';
import {
  Amount,
  ConnectCopy,
  CostQuery,
  CostSection,
  CostTile,
  CostTiles,
  CostToolbar,
  costStatus,
  dailyColumns,
  useCostQuery,
} from './CostParts';
import './index.scss';

// ---------------------------------------------------------------------------
// Pieces every metered provider shares
// ---------------------------------------------------------------------------

interface Budgeted {
  total_usage: number;
  monthly_limit_cents?: number;
  utilization_pct?: number | null;
  alert?: boolean;
}

const totalTile = (month: string, data: Budgeted): CostTile => ({
  label: 'Total spend',
  value: usd(data.total_usage),
  sublabel: monthLabel(month),
  icon: <PaymentsOutlinedIcon />,
});

/**
 * How much of the monthly limit is used. The tile's tone is the only colour on the page that
 * carries meaning, so it is the only one that changes: green, amber from 60%, red from 80% —
 * the same thresholds the old progress bar used. The number itself stays neutral; a red "Total
 * spend" read as an error when it was simply this month's bill.
 */
const budgetTile = (data: Budgeted): CostTile | null => {
  if (data.monthly_limit_cents == null) return null;
  const pct = data.utilization_pct ?? Math.round((data.total_usage / Math.max(1, data.monthly_limit_cents)) * 100);
  return {
    label: 'Budget used',
    value: `${pct}%`,
    sublabel: `${usd(data.total_usage)} of ${usdWhole(data.monthly_limit_cents)}`,
    icon: <SavingsOutlinedIcon />,
    tone: budgetTone(pct),
  };
};

const BudgetAlert: React.FC<{ provider: string; data: Budgeted }> = ({ provider, data }) =>
  data.alert ? (
    <Alert severity="warning">
      {provider} spend has reached {data.utilization_pct}% of the {usdWhole(data.monthly_limit_cents)} monthly
      budget. Review usage or raise the limit.
    </Alert>
  ) : null;

const itemColumns = (noun: string): MetricColumn<LineItem>[] => [
  { key: 'name', header: noun, variant: 'name', render: (r) => r.name },
  { key: 'cost', header: 'Cost (USD)', align: 'right', render: (r) => <Amount cents={r.cost} />, sortValue: (r) => r.cost },
];

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------------------
// AWS and MongoDB Atlas (one response shape)
// ---------------------------------------------------------------------------

interface ProviderConfig {
  provider: string;
  /** Singular noun for one breakdown row: "service", "SKU". */
  noun: string;
  buildUrl: (month: string) => string;
  connect: ConnectCopy;
  footnote?: (data: ProviderUsage) => React.ReactNode;
}

const AWS: ProviderConfig = {
  provider: 'AWS',
  noun: 'service',
  buildUrl: (month) => {
    const { from, to } = monthRange(month);
    return `/api/aws-costs?from=${from}&to=${to}`;
  },
  connect: {
    body: 'Give the Control Center server AWS credentials that can read Cost Explorer, and make sure Cost Explorer is turned on for the account.',
  },
  footnote: (data) =>
    [
      'AWS figures are read at most every six hours, since each Cost Explorer request is billed.',
      data.forecast && data.forecast.state !== 'ok' ? `Forecast: ${data.forecast.message}` : null,
    ]
      .filter(Boolean)
      .join(' '),
};

const ATLAS: ProviderConfig = {
  provider: 'MongoDB Atlas',
  noun: 'SKU',
  buildUrl: (month) => `/api/atlas-costs?month=${month}`,
  connect: {
    body: 'Add a read-only MongoDB Atlas organisation API key, and the organisation it belongs to, to the Control Center server.',
  },
};

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const ProviderCostPanel: React.FC<{ config: ProviderConfig }> = ({ config }) => {
  const query = useCostQuery<ProviderUsage>(config.buildUrl, `Failed to fetch ${config.provider} costs`);
  const status = costStatus(query as CostQuery<unknown>, config.provider, config.connect);
  const data = isProblem(query.data) ? null : query.data;

  const items = breakdownRows(data?.service_breakdown);
  const daily = providerDailyRows(data?.daily_costs);
  const top = items[0];
  const Noun = capitalise(config.noun);

  const tiles: CostTile[] = data
    ? ([
        totalTile(query.month, data),
        {
          // The amount is the value and the name the sublabel: AWS service names run to forty
          // characters, which at tile-value size wrapped to three lines and broke the row.
          label: `Top ${config.noun}`,
          value: top ? usd(top.cost) : '-',
          sublabel: top?.name,
          icon: <LeaderboardOutlinedIcon />,
        },
        data.forecast?.state === 'ok'
          ? { label: 'Forecast', value: usd(data.forecast.cents), sublabel: 'Month end, AWS forecast', icon: <TrendingUpOutlinedIcon /> }
          : { label: `${Noun}s with spend`, value: String(items.length), sublabel: monthLabel(query.month), icon: <CategoryOutlinedIcon /> },
        budgetTile(data),
      ].filter(Boolean) as CostTile[])
    : [];
  const projects = breakdownRows(data?.project_breakdown);
  const clusters = (data?.cluster_breakdown ?? []).filter((c) => c.cost > 0);

  return (
    <>
      <CostToolbar query={query as CostQuery<unknown>} />
      {status ?? (
        <>
          <BudgetAlert provider={config.provider} data={data!} />
          <CostTiles tiles={tiles} />
          <CostSection<LineItem>
            title={`Cost by ${config.noun}`}
            icon={<TableRowsOutlinedIcon />}
            meta={plural(items.length, config.noun)}
            columns={itemColumns(Noun)}
            rows={items}
            getRowKey={(r, i) => `${r.name}-${i}`}
          />
          {data?.project_breakdown && (
            <CostSection<LineItem>
              title="Cost by project"
              icon={<AccountTreeOutlinedIcon />}
              meta={plural(projects.length, 'project')}
              columns={itemColumns('Project')}
              rows={projects}
              getRowKey={(r, i) => `${r.name}-${i}`}
              footnote={
                data.project_names_resolved === false
                  ? 'Project names could not be read with this API key, so projects are shown by id. Add the Organization Read Only role to show names.'
                  : undefined
              }
            />
          )}
          {data?.cluster_breakdown && (
            <CostSection<ClusterRow>
              title="Cost by cluster"
              icon={<DnsOutlinedIcon />}
              meta={plural(clusters.length, 'cluster')}
              columns={CLUSTER_COLUMNS}
              rows={clusters}
              getRowKey={(r, i) => `${r.project}-${r.cluster}-${i}`}
              footnote="Not cluster specific is spend Atlas bills to a project or the organisation rather than a cluster, such as data transfer and support."
            />
          )}
          <CostSection
            title="Daily breakdown"
            icon={<CalendarMonthOutlinedIcon />}
            meta={plural(daily.length, 'day')}
            columns={dailyColumns(config.noun)}
            rows={daily}
            getRowKey={(r) => r.id}
            footnote={config.footnote?.(data!)}
          />
        </>
      )}
    </>
  );
};

interface ClusterRow {
  project: string;
  cluster: string;
  cost: number;
}

const CLUSTER_COLUMNS: MetricColumn<ClusterRow>[] = [
  { key: 'cluster', header: 'Cluster', variant: 'name', render: (r) => r.cluster },
  { key: 'project', header: 'Project', align: 'left', render: (r) => r.project },
  { key: 'cost', header: 'Cost (USD)', align: 'right', render: (r) => <Amount cents={r.cost} />, sortValue: (r) => r.cost },
];

// ---------------------------------------------------------------------------
// OpenAI
// ---------------------------------------------------------------------------

const OPENAI_CONNECT: ConnectCopy = {
  body: 'Add an OpenAI organisation admin key to the Control Center server so it can read usage and spend.',
};

const openAiUrl = (month: string) => {
  const { from, to } = monthRange(month);
  return `/api/openai-costs?from=${from}&to=${to}`;
};

interface ModelRow extends LineItem {
  category: string;
}

const MODEL_COLUMNS: MetricColumn<ModelRow>[] = [
  { key: 'name', header: 'Model', variant: 'name', render: (r) => r.name },
  { key: 'category', header: 'Type', align: 'left', render: (r) => <Chip size="small" label={categoryLabel(r.category)} /> },
  { key: 'cost', header: 'Cost (USD)', align: 'right', render: (r) => <Amount cents={r.cost} />, sortValue: (r) => r.cost },
];

const OpenAIPanel: React.FC = () => {
  const query = useCostQuery<OpenAIUsage>(openAiUrl, 'Failed to fetch OpenAI costs');
  const status = costStatus(query as CostQuery<unknown>, 'OpenAI', OPENAI_CONNECT);
  const data = isProblem(query.data) ? null : query.data;

  const models: ModelRow[] = (data?.model_breakdown ?? [])
    .filter((m) => m.cost > 0)
    .map((m) => ({ name: lineItemLabel(m.name), cost: m.cost, category: m.category }))
    .sort((a, b) => b.cost - a.cost);
  const daily = openAiDailyRows(data?.daily_costs);

  const tiles: CostTile[] = data
    ? ([
        totalTile(query.month, data),
        { label: 'Embeddings', value: usd(data.embedding_total ?? 0), sublabel: 'Search and matching vectors', icon: <DataObjectOutlinedIcon /> },
        { label: 'LLM', value: usd(data.llm_total ?? 0), sublabel: 'Generation and parsing', icon: <AutoAwesomeOutlinedIcon /> },
        budgetTile(data),
      ].filter(Boolean) as CostTile[])
    : [];

  return (
    <>
      <CostToolbar query={query as CostQuery<unknown>} />
      {status}
      {!status && data && (
        <>
          <BudgetAlert provider="OpenAI" data={data} />
          <CostTiles tiles={tiles} />
          <CostSection<ModelRow>
            title="Cost by model"
            icon={<TableRowsOutlinedIcon />}
            meta={plural(models.length, 'model')}
            columns={MODEL_COLUMNS}
            rows={models}
            getRowKey={(r, i) => `${r.name}-${i}`}
            footnote={
              models.some((m) => m.name === 'Unattributed')
                ? 'Unattributed is spend OpenAI reports without a model name, such as project-level charges. It is included in the total.'
                : undefined
            }
          />
        </>
      )}
      {/* The ledger is independent of OpenAI's admin key, so it shows even when billing is not connected. */}
      {!(query.loading && !query.data) && <AiUsageSection month={query.month} billedCents={data ? data.total_usage : null} />}
      {!status && data && (
        <CostSection
          title="Daily breakdown"
          icon={<CalendarMonthOutlinedIcon />}
          meta={plural(daily.length, 'day')}
          columns={dailyColumns('model')}
          rows={daily}
          getRowKey={(r) => r.id}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Page — tabs are route-driven (/cloud-costs/:tab)
// ---------------------------------------------------------------------------

const TAB_SLUG: Record<number, string> = { 0: 'all', 1: 'aws', 2: 'openai', 3: 'mongodb', 4: 'google' };
const SLUG_TAB: Record<string, number> = { all: 0, aws: 1, openai: 2, mongodb: 3, google: 4 };

const ApiCosts: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const activeTab = SLUG_TAB[tab ?? 'all'];

  /*
   * An unknown slug redirects; it does not silently render a provider. This used to be `?? 0`, and
   * the e2e suite spent months asserting against `/cloud-costs/overview` -- AWS under another name.
   */
  useEffect(() => {
    if (activeTab === undefined) navigate('/cloud-costs/all', { replace: true });
  }, [activeTab, navigate]);

  return (
    <CRMPageTemplate
      icon={<PaidOutlinedIcon />}
      title="Cloud & AI costs"
      subtitle="Monthly spend for each provider the platform pays for."
      tabs={{
        value: activeTab ?? 0,
        onChange: (v) => navigate(`/cloud-costs/${TAB_SLUG[v]}`, { replace: true }),
        tabs: [
          { label: 'All costs', icon: <SummarizeOutlinedIcon fontSize="small" /> },
          { label: 'AWS', icon: <CloudOutlinedIcon fontSize="small" /> },
          { label: 'OpenAI', icon: <AutoAwesomeOutlinedIcon fontSize="small" /> },
          { label: 'MongoDB Atlas', icon: <StorageOutlinedIcon fontSize="small" /> },
          { label: 'Google Cloud', icon: <GoogleIcon fontSize="small" /> },
        ],
      }}
    >
      <Box className="ac-page">
        {/* Keyed by tab so each provider keeps its own month and never shows another's numbers. */}
        {activeTab === 0 && <AllCostsPanel onOpenProvider={(slug) => navigate(`/cloud-costs/${slug}`)} />}
        {activeTab === 1 && <ProviderCostPanel key="aws" config={AWS} />}
        {activeTab === 2 && <OpenAIPanel />}
        {activeTab === 3 && <ProviderCostPanel key="mongodb" config={ATLAS} />}
        {activeTab === 4 && <GooglePanel />}
      </Box>
    </CRMPageTemplate>
  );
};

export default ApiCosts;
